import React, { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { shallow } from 'zustand/shallow';

import { Button, Fade, Icon } from '@column/column-ui-kit';

import { Action, EventDetail, EventIcon, EventMeta, EventType, JSONButton } from '../Events';
import { ROUTE } from '~/app/routes';
import { EmptyState, SectionHeader, Table, TableColumn } from '~/components';
import { EventDetailModal } from '~/components/Dialog';
import { LogoLoading, RelativeTime } from '~/elements';
import { useNavigate } from '~/lib/navigation';
import {
  ApiKeyRepository,
  EntityRepository,
  EventRepository,
  ParsedEvent,
  PlatformRolePermissions,
  WebhookRepository,
  getEventACHType,
  getEventIcon,
  getEventRoute,
  getEventStatus,
  getEventSummary,
  getRawEventDirection,
  getRawEventType,
  parseEvent,
} from '~/repositories';
import { BankAccountRepository } from '~/repositories/BankAccountRepository';
import { clientWrapper } from '~/repositories/client/wrapper';
import { useHelpSidebarStore } from '~/stores/HelpSidebar';
import { useNotificationStore } from '~/stores/Notification';
import { useSessionStore } from '~/stores/Session';
import { Line } from '~/styles';

import {
  AcceptLegalAgreementModal,
  AcceptPlatformInviteModal,
  GetStarted,
  PinnedBankAccounts,
  SandboxBanner,
  TransferSnapshots,
} from './_components';

interface LocationState {
  action?: string;
}

type EventsTableRow = ParsedEvent & { action: React.ReactElement };

const StyledFade = styled(Fade)`
  margin-top: 10%;
`;

const EventsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const ViewAllButtonContainer = styled.div`
  width: 100%;
  padding: 0 var(--page-margin) 24px;
  box-sizing: border-box;
`;

export const PageHomeOverview: React.FC = () => {
  const { currentUser, isAuthorized, isSandbox } = useSessionStore();
  const location = useLocation();
  const addDangerNotification = useNotificationStore((s) => s.addDangerNotification);
  const openHelpSidebar = useHelpSidebarStore((s) => s.openHelpSidebar);
  const [entities, setEntities] = useState<boolean>(false);
  const [bankAccounts, setBankAccounts] = useState<boolean>(false);
  const [apiKeys, setApiKeys] = useState<boolean>(false);
  const [webhooks, setWebhooks] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [events, setEvents] = useState<EventsTableRow[]>([]);
  const [eventsLoading, setEventsLoading] = useState<boolean>(false);
  const [isEventDetailModalOpen, setIsEventDetailModalOpen] = useState(false);
  const [selectedEventData, setSelectedEventData] = useState<any>(null);

  const handleOpenEventDetail = useCallback((data: any) => {
    setSelectedEventData(data);
    setIsEventDetailModalOpen(true);
  }, []);

  const state = location.state as LocationState;

  const fetchData = (defaultPlatformId: string, currentPermissionState: PlatformRolePermissions) => {
    const promises = [];

    promises.push(
      EntityRepository.getAll().then((response) => {
        setEntities(response.entities.length > 0);
      })
    );

    promises.push(
      BankAccountRepository.getAll().then((response) => {
        setBankAccounts(response.bankAccounts.length > 0);
      })
    );

    if (currentPermissionState?.apiKeys === 'read' || currentPermissionState?.apiKeys === 'write') {
      promises.push(
        ApiKeyRepository.getAll(defaultPlatformId).then((response) => {
          setApiKeys(response.apiKeys.length > 0);
        })
      );
    } else {
      setApiKeys(false);
    }

    if (currentPermissionState?.webhooks === 'read' || currentPermissionState?.webhooks === 'write') {
      promises.push(
        WebhookRepository.getAll().then((response) => {
          setWebhooks(response.webhookEndpoints.length > 0);
        })
      );
    } else {
      setWebhooks(false);
    }

    clientWrapper(undefined, (e) => console.error('HomeFetchData', e), Promise.all(promises)).finally(() =>
      setLoading(false)
    );
  };

  useEffect(
    () =>
      useSessionStore.subscribe(
        (s) => [s.isLoading, s.isSandbox, s.currentUser?.defaultPlatformId, s.currentPermission],
        (s) => {
          const isLoading = s[0] as boolean | undefined;
          const defaultPlatformId = s[2] as string | undefined;
          const currentPermissionState = s[3] as PlatformRolePermissions | undefined;

          if (isLoading || !defaultPlatformId || !currentPermissionState) {
            return;
          }

          fetchData(defaultPlatformId, currentPermissionState);
        },
        {
          fireImmediately: true,
          equalityFn: shallow,
        }
      ),
    []
  );

  useEffect(() => {
    if (state && state.action === 'platformInfoPermissionError') {
      addDangerNotification({
        content: "You got redirected, because you don't have permissions to view this page.",
        actionButton: {
          label: 'Roles',
          onClick: () => openHelpSidebar('Roles'),
        },
      });
    }
  }, [location]);

  useEffect(() => {
    if (!isAuthorized({ permission: 'webhooks', permissionLevel: 'read' })) {
      return;
    }

    if (!currentUser?.defaultPlatformId) {
      return;
    }

    setEventsLoading(true);
    EventRepository.getAll({ limit: 50 })
      .then((response) => {
        const entries = response.events.map(
          (event): EventsTableRow => ({
            ...parseEvent(event),
            action: (
              <Action>
                <JSONButton
                  size="tiny"
                  variant="muted"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleOpenEventDetail(event.data);
                  }}
                >
                  JSON
                </JSONButton>
              </Action>
            ),
          })
        );
        setEvents(entries);
      })
      .catch((e) => {
        console.error('Failed to fetch events', e);
      })
      .finally(() => {
        setEventsLoading(false);
      });
  }, [currentUser?.defaultPlatformId, handleOpenEventDetail]);

  const columns: TableColumn<EventsTableRow>[] = [
    {
      Header: 'Type',
      accessor: 'data',
      width: 'minmax(300px, auto)',
      Cell: (current) => (
        <EventMeta>
          <EventType>
            {getRawEventDirection(current.value?.eventType ?? '')} {getRawEventType(current.value?.eventType ?? '')}{' '}
            {getEventACHType(current.value)}
          </EventType>

          <EventDetail>
            <EventIcon>{getEventIcon(current.value?.eventType ?? '')}</EventIcon>

            <span>{getEventSummary(current.value)}</span>
          </EventDetail>
        </EventMeta>
      ),
    },
    {
      Header: 'Status',
      accessor: 'eventType',
      width: 'minmax(min-content, 100px)',
      Cell: (current) => getEventStatus(current.value),
    },
    {
      Header: 'Created',
      accessor: 'createdAt',
      sortType: 'datetime',
      width: 'min-content',
      Cell: (current) => <RelativeTime timestamp={current.value} />,
    },
    {
      Header: '',
      accessor: 'action',
      width: 'min-content',
    },
  ];

  const navigate = useNavigate();

  return (
    <>
      <EventDetailModal
        isOpen={isEventDetailModalOpen}
        onClose={() => setIsEventDetailModalOpen(false)}
        data={selectedEventData}
      />
      <AcceptPlatformInviteModal />
      <AcceptLegalAgreementModal />
      <StyledFade show={loading}>
        <LogoLoading />
      </StyledFade>
      <Fade show={!loading}>
        {isSandbox && <SandboxBanner />}

        <GetStarted
          showVerifyEmail={!currentUser?.isEmailVerified}
          showGenerateApiKeys={isAuthorized({ permission: 'apiKeys', permissionLevel: 'read' })}
          showSetupMfa={!currentUser?.factors.some((factor) => factor.factorVerified)}
          showAddBankAccount={
            !bankAccounts && entities && isAuthorized({ permission: 'bankAccounts', permissionLevel: 'read' })
          }
          showAddEntity={!entities && isAuthorized({ permission: 'entities', permissionLevel: 'read' })}
          showAddWebhook={!webhooks && apiKeys && isAuthorized({ permission: 'webhooks', permissionLevel: 'read' })}
          showGettingStartedGuide
        />

        <PinnedBankAccounts />

        <Line />

        <TransferSnapshots />

        {isAuthorized({ permission: 'webhooks', permissionLevel: 'read' }) && (
          <>
            <SectionHeader text="Recent Events" border />

            <EventsContainer>
              {events.length === 0 && !eventsLoading ? (
                <EmptyState headline="No events found" text="Create your first person or a business entity.">
                  <Button onClick={() => navigate(`${ROUTE.ENTITIES}/edit/person`)} size="small" icon={<Icon.User />}>
                    Create Entity
                  </Button>
                </EmptyState>
              ) : (
                <>
                  <Table
                    columns={columns}
                    data={events}
                    isLoading={eventsLoading}
                    onRowClick={({ original }: { original: EventsTableRow }) =>
                      navigate(
                        getRawEventType(original.eventType) === 'Identity'
                          ? `${getEventRoute(original.eventType)}/${(original.data.subType ?? 'undefined').toLowerCase()}/${original.data.id}`
                          : getRawEventType(original.eventType) === undefined
                            ? ''
                            : `${getEventRoute(original.eventType)}/${original.data.id}`
                      )
                    }
                  />
                  <ViewAllButtonContainer>
                    <Button variant="muted" fullWidth onClick={() => navigate(ROUTE.EVENTS)}>
                      View All Events
                    </Button>
                  </ViewAllButtonContainer>
                </>
              )}
            </EventsContainer>
          </>
        )}
      </Fade>
    </>
  );
};
